/* eslint-disable react/jsx-no-bind -- DOM-fallback hover handlers are per-feature closures inside a map; useCallback can't close over the loop item */
// Renders container features as WebGL deck.gl pills or DOM AdvancedMarker chips
// (ContainerMarker) depending on WebGL support — mirrors AdaptiveBulkMarkerLayer.
import PropTypes from 'prop-types'
import React from 'react'

import { useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import WebGLMarkerSurface from 'components/common/map/webgl/WebGLMarkerSurface'
import ContainerMarker from 'components/inventory/containers/tabpanel/map/ContainerMarker'

import DeckContainerMarkerRenderer from './DeckContainerMarkerRenderer'

// JSDoc so TS consumers aren't held to the empty-array default's `never[]`.
/**
 * @param {{
 *   features?: object[], onClick?: Function, onHover?: Function,
 *   pane?: string, zIndex?: number,
 * }} props
 */
const AdaptiveContainerMarkerLayer = ({ features = [], onClick, onHover, pane, zIndex }) => {
  const webglSupported = useWebGLSupport()

  // Funnel through WebGLMarkerSurface so an async WebGL crash degrades to DOM
  // ContainerMarker via the shared error boundary.
  const domMarkers = (
    <>
      {features.map(feature => {
        const properties = feature.properties || {}
        const [lng, lat] = feature.geometry?.coordinates || []
        // Master-route features only carry coords on geometry; ContainerMarker reads latitude/longitude from props.
        const container = {
          ...properties,
          latitude: properties.latitude ?? lat,
          longitude: properties.longitude ?? lng,
          // `_hideLabel` (a caller's label-spacing thinning) reaches the DOM chip by
          // emptying the fields it builds its label from, since it derives the label
          // itself rather than taking one.
          // eslint-disable-next-line no-underscore-dangle -- the marker layer's own private label-suppression channel
          ...(properties._hideLabel ? { measure: null, containerName: '', name: '' } : {}),
        }
        const wrapEnter = onHover ? e => onHover(feature, { clientX: e.clientX, clientY: e.clientY }) : undefined
        const wrapLeave = onHover ? () => onHover(null, null) : undefined
        return <ContainerMarker key={container.id} container={container} onMouseEnter={wrapEnter} onMouseLeave={wrapLeave} />
      })}
    </>
  )

  return (
    <WebGLMarkerSurface componentName="AdaptiveContainerMarkerLayer" enabled={webglSupported} fallback={domMarkers}>
      <DeckContainerMarkerRenderer features={features} pane={pane} zIndex={zIndex} onClick={onClick} onHover={onHover} />
    </WebGLMarkerSurface>
  )
}

AdaptiveContainerMarkerLayer.propTypes = {
  features: PropTypes.array,
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  pane: PropTypes.oneOf(['floatPane', 'overlayMouseTarget', 'overlayLayer']),
  zIndex: PropTypes.number,
}

export default AdaptiveContainerMarkerLayer
